// web/src/pages/Admin.tsx
// FRAME Admin Panel — Sandstone Cloud internal dashboard
// Real analytics backed by DynamoDB via admin Lambda, with fallback demo data
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { WebAuthService } from '../services/auth';

// ─── Design tokens ──────────────────────────────────────────
const T = {
  navy: '#0f172a', surface: '#1e293b', surfaceHover: '#334155',
  blue: '#2563eb', blueLight: '#3b82f6',
  teal: '#50B8A8', tealLight: '#7CCFC3',
  textPrimary: '#f8fafc', textSecondary: '#94a3b8', textMuted: '#64748b',
  border: '#334155', error: '#ef4444', success: '#22c55e',
} as const;

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const API_URL = import.meta.env.VITE_API_URL || '';

// ─── Cognito admin check ────────────────────────────────────
async function checkAdminGroup(): Promise<boolean> {
  try {
    const session = await WebAuthService.getSession();
    const groups = (session?.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];
    if (groups.includes('frame-admins')) return true;
    // Domain-based admin: any verified @sandstonecloud.com email
    const email = (session?.tokens?.idToken?.payload?.email as string) ?? '';
    return email.toLowerCase().endsWith('@sandstonecloud.com');
  } catch { return false; }
}

async function adminQuery(action: string): Promise<any> {
  try {
    const session = await WebAuthService.getSession();
    const token = session?.tokens?.idToken?.toString();
    if (!token || !API_URL) return null;
    const res = await fetch(`${API_URL}/admin/analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

function scoreColor(score: number): string {
  if (score < 3) return '#EF4444';
  if (score < 5) return '#F59E0B';
  if (score < 7) return '#50B8A8';
  return '#059669';
}

// ─── Fallback demo data ─────────────────────────────────────
const FALLBACK_STATS = { totalUsers: 12847, totalSubscriptions: 4291, totalScores: 284021 };
const FALLBACK_SCORE_DIST = { bins: [
  { range: '0-1', count: 142 }, { range: '1-2', count: 891 }, { range: '2-3', count: 2340 },
  { range: '3-4', count: 8721 }, { range: '4-5', count: 18450 }, { range: '5-6', count: 42100 },
  { range: '6-7', count: 58300 }, { range: '7-8', count: 45200 }, { range: '8-9', count: 21800 },
  { range: '9-10', count: 4890 },
]};
const FALLBACK_DIM_AVG = { portraitComposition: 6.2, lighting: 5.8, composition: 6.5, sceneInterest: 6.1 };
const FALLBACK_MODES = { modes: [
  { mode: 'balanced', count: 82400, averageScore: 6.4 },
  { mode: 'social', count: 61200, averageScore: 6.1 },
  { mode: 'instagram', count: 48900, averageScore: 6.8 },
  { mode: 'travel', count: 31200, averageScore: 7.1 },
  { mode: 'professional', count: 28700, averageScore: 5.9 },
  { mode: 'realEstate', count: 18400, averageScore: 6.7 },
  { mode: 'event', count: 12100, averageScore: 6.3 },
  { mode: 'sports', count: 9800, averageScore: 6.6 },
  { mode: 'custom', count: 5400, averageScore: 6.9 },
]};
const FALLBACK_DETRACTORS = { buckets: [
  { range: 'none', count: 142000 }, { range: '0-0.1', count: 58000 },
  { range: '0.1-0.3', count: 41000 }, { range: '0.3-0.5', count: 22000 },
  { range: '0.5+', count: 8200 },
]};
const FALLBACK_LANDMARKS = { tiers: [
  { tier: 0, count: 198000 }, { tier: 1, count: 42000 },
  { tier: 2, count: 18400 }, { tier: 3, count: 5200 },
]};
const FALLBACK_USERS = { tiers: [
  { tier: 'free', count: 8556 }, { tier: 'silver', count: 2891 }, { tier: 'gold', count: 1400 },
]};
const FALLBACK_RECENT: any = { scores: [] };
const FALLBACK_FEEDBACK = { items: [
  { id: '1', message: 'Love the transparent scoring! Finally an app that shows me why my photos scored the way they did.', email: 'alex@gmail.com', tier: 'Free', mode: 'balanced', createdAt: '2026-04-05T10:30:00Z', status: 'new', context: { userAgent: 'iPhone Safari 18', screen: '390x844' } },
  { id: '2', message: 'PRISM toggle was confusing at first but once I figured it out, the personalized scoring is amazing.', email: 'sarah.m@outlook.com', tier: 'Silver', mode: 'social', createdAt: '2026-04-04T15:20:00Z', status: 'reviewed', context: { userAgent: 'Chrome 124', screen: '1920x1080' } },
  { id: '3', message: 'Would love a landscape-only mode for nature photography.', email: 'mike.photo@gmail.com', tier: 'Gold', mode: 'professional', createdAt: '2026-04-03T09:15:00Z', status: 'new', context: { userAgent: 'Firefox 126', screen: '2560x1440' } },
  { id: '4', message: 'Sports mode nails the action shots. Perfect for my kids soccer games.', email: 'dadcam@yahoo.com', tier: 'Free', mode: 'sports', createdAt: '2026-04-02T18:45:00Z', status: 'resolved', context: { userAgent: 'Chrome 124', screen: '1440x900' } },
  { id: '5', message: 'Real estate mode is exactly what we needed for our listings.', email: 'agent@remax.com', tier: 'Gold', mode: 'realEstate', createdAt: '2026-04-01T11:00:00Z', status: 'reviewed', context: { userAgent: 'Safari 18', screen: '1512x982' } },
]};
const FALLBACK_ACTIVITY = {
  dau: 1842, wau: 8921, mau: 28400,
  photosToday: 12480, photosWeek: 68200, photosAllTime: 284021,
  anonymousPercent: 62, authenticatedPercent: 38,
  avgSessionDuration: 4.2, // minutes
  freeTrialConversions: 847, conversionRate: 6.6,
  prismAdoption: 34.2,
  topModes: [
    { mode: 'balanced', pct: 29 }, { mode: 'social', pct: 22 }, { mode: 'instagram', pct: 17 },
    { mode: 'travel', pct: 11 }, { mode: 'professional', pct: 10 }, { mode: 'realEstate', pct: 6 },
    { mode: 'event', pct: 3 }, { mode: 'sports', pct: 1.5 }, { mode: 'custom', pct: 0.5 },
  ],
};

// ─── Static calibration data ────────────────────────────────
const MODE_WEIGHTS: Record<string, { portrait: number; lighting: number; composition: number; scene: number }> = {
  balanced: { portrait: 0.30, lighting: 0.25, composition: 0.25, scene: 0.20 },
  social: { portrait: 0.40, lighting: 0.25, composition: 0.18, scene: 0.17 },
  instagram: { portrait: 0.30, lighting: 0.22, composition: 0.23, scene: 0.25 },
  travel: { portrait: 0.15, lighting: 0.20, composition: 0.25, scene: 0.40 },
  realEstate: { portrait: 0.00, lighting: 0.30, composition: 0.40, scene: 0.30 },
  professional: { portrait: 0.40, lighting: 0.30, composition: 0.20, scene: 0.10 },
  event: { portrait: 0.25, lighting: 0.20, composition: 0.25, scene: 0.30 },
  sports: { portrait: 0.20, lighting: 0.25, composition: 0.30, scene: 0.25 },
  custom: { portrait: 0.25, lighting: 0.25, composition: 0.25, scene: 0.25 },
};
const LANDMARK_COEFFS = [
  { tier: 'Iconic', coefficient: 1.30 }, { tier: 'Notable', coefficient: 1.20 },
  { tier: 'Interesting', coefficient: 1.10 }, { tier: 'Ordinary', coefficient: 1.00 },
];
const DETRACTOR_WEIGHTS = [
  { name: 'Blur', weight: 0.40 }, { name: 'Closed Eyes', weight: 0.30 },
  { name: 'Motion Blur', weight: 0.25 }, { name: 'Severe Exposure', weight: 0.25 },
  { name: 'Red Eye', weight: 0.15 }, { name: 'Obstruction', weight: 0.20 },
];
const TIER_NAMES: Record<number, string> = { 0: 'Ordinary', 1: 'Interesting', 2: 'Notable', 3: 'Iconic' };
const DIM_COLORS = { portrait: '#8b5cf6', lighting: '#f59e0b', composition: '#3b82f6', scene: '#50B8A8' };

// ─── Inject global styles ───────────────────────────────────
const injectAdminCSS = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('frame-admin-css')) return;
  const style = document.createElement('style');
  style.id = 'frame-admin-css';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${T.navy}; }
    input::placeholder { color: ${T.textMuted}; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: ${T.navy}; }
    ::-webkit-scrollbar-thumb { background: ${T.surfaceHover}; border-radius: 3px; }
  `;
  document.head.appendChild(style);
};

type NavSection = 'dashboard' | 'scoring' | 'modes' | 'users' | 'feedback' | 'activity' | 'calibration' | 'health';

// ─── Component ──────────────────────────────────────────────
export function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<NavSection>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  // Data states
  const [dashData, setDashData] = useState<any>(null);
  const [scoreDistData, setScoreDistData] = useState<any>(null);
  const [dimAvgData, setDimAvgData] = useState<any>(null);
  const [detractorData, setDetractorData] = useState<any>(null);
  const [landmarkData, setLandmarkData] = useState<any>(null);
  const [recentData, setRecentData] = useState<any>(null);
  const [modesData, setModesData] = useState<any>(null);
  const [usersData, setUsersData] = useState<any>(null);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [activityData, setActivityData] = useState<any>(null);
  const [expandedFeedback, setExpandedFeedback] = useState<string | null>(null);
  const [sectionLoading, setSectionLoading] = useState(false);

  useEffect(() => {
    injectAdminCSS();
    const check = () => setIsMobile(window.innerWidth < 768);
    check(); window.addEventListener('resize', check);

    // Check existing session
    (async () => {
      try {
        const user = await WebAuthService.getCurrentUser();
        if (user && await checkAdminGroup()) setIsAuthenticated(true);
      } catch {}
      setAuthLoading(false);
    })();

    return () => window.removeEventListener('resize', check);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(''); setLoginLoading(true);
    try {
      await WebAuthService.signIn(email, password);
      if (await checkAdminGroup()) {
        setIsAuthenticated(true);
      } else {
        await WebAuthService.signOut();
        setLoginError('Access restricted to Sandstone Cloud team members.');
      }
    } catch (err: any) {
      setLoginError(err?.message || 'Login failed.');
    } finally { setLoginLoading(false); }
  };

  const handleSignOut = async () => {
    await WebAuthService.signOut();
    setIsAuthenticated(false); setEmail(''); setPassword('');
    setDashData(null); setScoreDistData(null); setDimAvgData(null);
    setDetractorData(null); setLandmarkData(null); setRecentData(null);
    setModesData(null); setUsersData(null);
  };

  // Data loaders with fallback
  const loadDashboard = useCallback(async () => {
    if (dashData) return;
    setSectionLoading(true);
    const data = await adminQuery('getStats');
    if (data) { setDashData(data); setUsingFallback(false); }
    else { setDashData(FALLBACK_STATS); setUsingFallback(true); }
    setSectionLoading(false);
  }, [dashData]);

  const loadScoring = useCallback(async () => {
    if (scoreDistData) return;
    setSectionLoading(true);
    const [dist, dims, det, lm, rec] = await Promise.all([
      adminQuery('getScoreDistribution'), adminQuery('getDimensionAverages'),
      adminQuery('getDetractorStats'), adminQuery('getLandmarkDistribution'),
      adminQuery('getRecentScores'),
    ]);
    const fallback = !dist;
    setScoreDistData(dist ?? FALLBACK_SCORE_DIST);
    setDimAvgData(dims ?? FALLBACK_DIM_AVG);
    setDetractorData(det ?? FALLBACK_DETRACTORS);
    setLandmarkData(lm ?? FALLBACK_LANDMARKS);
    setRecentData(rec ?? FALLBACK_RECENT);
    setUsingFallback(fallback);
    setSectionLoading(false);
  }, [scoreDistData]);

  const loadModes = useCallback(async () => {
    if (modesData) return;
    setSectionLoading(true);
    const data = await adminQuery('getModeBreakdown');
    if (data) { setModesData(data); setUsingFallback(false); }
    else { setModesData(FALLBACK_MODES); setUsingFallback(true); }
    setSectionLoading(false);
  }, [modesData]);

  const loadUsers = useCallback(async () => {
    if (usersData) return;
    setSectionLoading(true);
    const data = await adminQuery('getUserStats');
    if (data) { setUsersData(data); setUsingFallback(false); }
    else { setUsersData(FALLBACK_USERS); setUsingFallback(true); }
    setSectionLoading(false);
  }, [usersData]);

  const loadFeedback = useCallback(async () => {
    if (feedbackData) return;
    setSectionLoading(true);
    const data = await adminQuery('getFeedback');
    if (data) { setFeedbackData(data); setUsingFallback(false); }
    else { setFeedbackData(FALLBACK_FEEDBACK); setUsingFallback(true); }
    setSectionLoading(false);
  }, [feedbackData]);

  const loadActivity = useCallback(async () => {
    if (activityData) return;
    setSectionLoading(true);
    const data = await adminQuery('getActivity');
    if (data) { setActivityData(data); setUsingFallback(false); }
    else { setActivityData(FALLBACK_ACTIVITY); setUsingFallback(true); }
    setSectionLoading(false);
  }, [activityData]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeSection === 'dashboard') loadDashboard();
    else if (activeSection === 'scoring') loadScoring();
    else if (activeSection === 'modes') loadModes();
    else if (activeSection === 'users') loadUsers();
    else if (activeSection === 'feedback') loadFeedback();
    else if (activeSection === 'activity') loadActivity();
  }, [activeSection, isAuthenticated, loadDashboard, loadScoring, loadModes, loadUsers, loadFeedback, loadActivity]);

  // ─── Login gate ───────────────────────────────────────────
  if (authLoading) {
    return (<div style={{ minHeight: '100vh', background: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <div style={{ color: T.textMuted, fontSize: 14 }}>Loading...</div>
    </div>);
  }

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', background: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, padding: 20 }}>
        <div style={{ background: T.surface, borderRadius: 16, padding: 40, width: '100%', maxWidth: 420, border: `1px solid ${T.border}` }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: T.textPrimary, letterSpacing: 3, marginBottom: 4 }}>FRAME</div>
            <div style={{ fontSize: 13, color: T.teal, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase' }}>Admin Panel</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>Sandstone Cloud Internal</div>
          </div>
          <form onSubmit={handleLogin}>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: T.textSecondary, display: 'block', marginBottom: 6 }}>Email</span>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@sandstonecloud.com"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.navy, color: T.textPrimary, fontSize: 14, fontFamily: FONT, outline: 'none' }} />
            </label>
            <label style={{ display: 'block', marginBottom: 24 }}>
              <span style={{ fontSize: 13, color: T.textSecondary, display: 'block', marginBottom: 6 }}>Password</span>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.navy, color: T.textPrimary, fontSize: 14, fontFamily: FONT, outline: 'none' }} />
            </label>
            {loginError && (
              <div style={{ color: T.error, fontSize: 13, marginBottom: 16, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{loginError}</div>
            )}
            <button type="submit" disabled={loginLoading} style={{ width: '100%', padding: '12px 0', borderRadius: 8, background: 'linear-gradient(135deg, #50B8A8 0%, #3B8CB8 50%, #5186B0 100%)', color: T.textPrimary, fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: FONT, opacity: loginLoading ? 0.6 : 1, boxShadow: '0 2px 12px rgba(80,184,168,0.25), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Helper renderers ─────────────────────────────────────
  const card = (title: string, children: React.ReactNode) => (
    <div style={{ background: T.surface, borderRadius: 12, padding: 24, border: `1px solid ${T.border}`, marginBottom: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );

  const hBar = (label: string, value: number, maxVal: number, color: string) => (
    <div key={label} style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span style={{ color: T.textSecondary }}>{label}</span>
        <span style={{ color: T.textPrimary, fontWeight: 600 }}>{typeof value === 'number' ? (value > 100 ? value.toLocaleString() : value.toFixed(1)) : value}</span>
      </div>
      <div style={{ background: T.navy, borderRadius: 4, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min((value / maxVal) * 100, 100)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );

  const loadingView = () => (
    <div style={{ padding: 60, textAlign: 'center', color: T.textMuted, fontSize: 14 }}>Loading data...</div>
  );

  // ─── Section renderers ────────────────────────────────────
  const navItems: { key: NavSection; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '\u25A1' },
    { key: 'activity', label: 'Activity', icon: '\u25A5' },
    { key: 'feedback', label: 'Feedback', icon: '\u25A8' },
    { key: 'scoring', label: 'Scoring Analytics', icon: '\u25C6' },
    { key: 'modes', label: 'Modes', icon: '\u25EB' },
    { key: 'users', label: 'Users', icon: '\u25C9' },
    { key: 'calibration', label: 'Calibration & IP', icon: '\u2699' },
    { key: 'health', label: 'System Health', icon: '\u2665' },
  ];

  const renderDashboard = () => {
    if (sectionLoading || !dashData) return loadingView();
    const stats = [
      { label: 'Total Users', value: dashData.totalUsers?.toLocaleString() ?? '—' },
      { label: 'Active Subscriptions', value: dashData.totalSubscriptions?.toLocaleString() ?? '—' },
      { label: 'Total Scores', value: dashData.totalScores?.toLocaleString() ?? '—' },
      { label: 'Scores Today', value: dashData.scoresToday?.toLocaleString() ?? '—' },
    ];
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>Dashboard Overview</h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          {stats.map(s => (
            <div key={s.label} style={{ background: T.surface, borderRadius: 12, padding: 20, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderScoring = () => {
    if (sectionLoading || !scoreDistData) return loadingView();
    const bins = scoreDistData.bins || [];
    const maxBin = Math.max(...bins.map((b: any) => b.count), 1);

    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>Scoring Analytics</h2>

        {card('Composite Score Distribution', (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: isMobile ? 4 : 12, height: 200, paddingBottom: 28, position: 'relative' }}>
            {bins.map((b: any, i: number) => {
              const pct = (b.count / maxBin) * 100;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                  <div style={{ fontSize: 10, color: T.textSecondary, marginBottom: 4 }}>{b.count.toLocaleString()}</div>
                  <div style={{ width: '100%', maxWidth: 48, borderRadius: '4px 4px 0 0', background: scoreColor(i + 0.5), height: `${Math.max(pct, 2)}%`, transition: 'height 0.4s ease' }} />
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6, position: 'absolute', bottom: 0 }}>{i}</div>
                </div>
              );
            })}
          </div>
        ))}

        {card('Dimension Averages', (
          <div>
            {dimAvgData && [
              { label: 'Portrait Composition', key: 'portraitComposition' },
              { label: 'Lighting', key: 'lighting' },
              { label: 'Composition', key: 'composition' },
              { label: 'Scene Interest', key: 'sceneInterest' },
            ].map(d => hBar(d.label, dimAvgData[d.key] ?? 0, 10, scoreColor(dimAvgData[d.key] ?? 0)))}
          </div>
        ))}

        {card('Detractor Penalty Distribution', (
          <div>
            {(detractorData?.buckets || []).map((b: any) => {
              const maxD = Math.max(...(detractorData?.buckets || []).map((x: any) => x.count), 1);
              return hBar(b.range === 'none' ? 'No penalty' : b.range, b.count, maxD, b.range === 'none' ? T.success : T.teal);
            })}
          </div>
        ))}

        {card('Landmark Tier Distribution', (
          <div>
            {(landmarkData?.tiers || []).map((t: any) => {
              const maxL = Math.max(...(landmarkData?.tiers || []).map((x: any) => x.count), 1);
              const colors: Record<number, string> = { 0: T.textMuted, 1: T.success, 2: T.blueLight, 3: T.teal };
              return hBar(TIER_NAMES[t.tier] || `Tier ${t.tier}`, t.count, maxL, colors[t.tier] || T.textMuted);
            })}
          </div>
        ))}

        {card('Recent Scores', (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>{['Score', 'Mode', 'Portrait', 'Lighting', 'Comp.', 'Scene', 'Penalty', 'Landmark', 'Date'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: T.textMuted, fontWeight: 500, borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(recentData?.scores || []).slice(0, 20).map((s: any, i: number) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '8px 10px', color: scoreColor(s.compositeScore), fontWeight: 600 }}>{s.compositeScore?.toFixed(1)}</td>
                    <td style={{ padding: '8px 10px', color: T.textSecondary }}>{s.mode}</td>
                    <td style={{ padding: '8px 10px', color: T.textSecondary }}>{s.portraitComposition?.toFixed(1) ?? '—'}</td>
                    <td style={{ padding: '8px 10px', color: T.textSecondary }}>{s.lighting?.toFixed(1)}</td>
                    <td style={{ padding: '8px 10px', color: T.textSecondary }}>{s.composition?.toFixed(1)}</td>
                    <td style={{ padding: '8px 10px', color: T.textSecondary }}>{s.sceneInterest?.toFixed(1)}</td>
                    <td style={{ padding: '8px 10px', color: s.detractorPenalty > 0.3 ? T.error : T.textMuted }}>{s.detractorPenalty?.toFixed(2)}</td>
                    <td style={{ padding: '8px 10px', color: T.textSecondary }}>{TIER_NAMES[s.landmarkTier] ?? s.landmarkTier}</td>
                    <td style={{ padding: '8px 10px', color: T.textMuted }}>{s.createdAt?.slice(0, 10) ?? '—'}</td>
                  </tr>
                ))}
                {(!recentData?.scores || recentData.scores.length === 0) && (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No scores recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  const renderModes = () => {
    if (sectionLoading || !modesData) return loadingView();
    const modes = modesData.modes || [];
    const maxCount = Math.max(...modes.map((m: any) => m.count), 1);

    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>Mode Analytics</h2>

        {card('Mode Popularity', (
          <div>{modes.sort((a: any, b: any) => b.count - a.count).map((m: any) => hBar(m.mode, m.count, maxCount, T.blueLight))}</div>
        ))}

        {card('Average Score by Mode', (
          <div>{modes.map((m: any) => hBar(m.mode, m.averageScore, 10, scoreColor(m.averageScore)))}</div>
        ))}

        {card('Weight Profiles (Patent Reference)', (
          <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 11, color: T.textMuted }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: DIM_COLORS.portrait, marginRight: 4 }} />Portrait</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: DIM_COLORS.lighting, marginRight: 4 }} />Lighting</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: DIM_COLORS.composition, marginRight: 4 }} />Composition</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: DIM_COLORS.scene, marginRight: 4 }} />Scene Interest</span>
            </div>
            {Object.entries(MODE_WEIGHTS).map(([mode, w]) => (
              <div key={mode} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>{mode}</div>
                <div style={{ display: 'flex', height: 16, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${w.portrait * 100}%`, background: DIM_COLORS.portrait }} />
                  <div style={{ width: `${w.lighting * 100}%`, background: DIM_COLORS.lighting }} />
                  <div style={{ width: `${w.composition * 100}%`, background: DIM_COLORS.composition }} />
                  <div style={{ width: `${w.scene * 100}%`, background: DIM_COLORS.scene }} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderUsers = () => {
    if (sectionLoading || !usersData) return loadingView();
    const tiers = usersData.tiers || [];
    const tierColors: Record<string, string> = { free: T.textMuted, silver: T.textSecondary, gold: T.teal };

    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>User Analytics</h2>
        {card('Tier Distribution', (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
            {tiers.map((t: any) => (
              <div key={t.tier} style={{ background: T.navy, borderRadius: 12, padding: 20, textAlign: 'center', border: `1px solid ${tierColors[t.tier] || T.border}40` }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: tierColors[t.tier] || T.textPrimary }}>{t.count?.toLocaleString()}</div>
                <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4, textTransform: 'capitalize' }}>{t.tier}</div>
              </div>
            ))}
          </div>
        ))}
        {card('Activity Summary', (
          <div style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.6 }}>
            Detailed per-user activity analytics available via DynamoDB console or future CloudWatch dashboards.
            Current tables: frame-users, frame-scores, frame-analytics (90-day TTL).
          </div>
        ))}
      </div>
    );
  };

  const renderCalibration = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>Calibration & IP Reference</h2>

      {card('Scoring Formula', (
        <div style={{ fontFamily: 'monospace', fontSize: 13, color: T.tealLight, background: T.navy, padding: 16, borderRadius: 8, lineHeight: 1.8 }}>
          composite = {'\u03A3'}(weight_i {'\u00D7'} dimension_i) {'\u00D7'} (1 - penalty {'\u00D7'} strength)<br />
          <span style={{ color: T.textMuted }}>// Multiplicative detractor penalty (Patent Claim 4)</span>
        </div>
      ))}

      {card('Landmark Spike Formula', (
        <div style={{ fontFamily: 'monospace', fontSize: 13, color: T.tealLight, background: T.navy, padding: 16, borderRadius: 8, lineHeight: 1.8 }}>
          spiked = min(10, base + (coefficient - 1.0) {'\u00D7'} strength {'\u00D7'} base)<br />
          <span style={{ color: T.textMuted }}>// Patent Claim 3 — Landmark spike coefficient system</span>
        </div>
      ))}

      {card('Landmark Coefficients', (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr>{['Tier', 'Coefficient', 'Spike'].map(h => (
            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.textMuted, fontWeight: 500, borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
          ))}</tr></thead>
          <tbody>{LANDMARK_COEFFS.map(l => (
            <tr key={l.tier} style={{ borderBottom: `1px solid ${T.border}` }}>
              <td style={{ padding: '8px 12px', color: T.textPrimary }}>{l.tier}</td>
              <td style={{ padding: '8px 12px', color: T.teal, fontFamily: 'monospace' }}>{l.coefficient.toFixed(2)}</td>
              <td style={{ padding: '8px 12px', color: T.textSecondary, fontFamily: 'monospace' }}>+{((l.coefficient - 1) * 100).toFixed(0)}%</td>
            </tr>
          ))}</tbody>
        </table>
      ))}

      {card('Detractor Penalty Weights', (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr>{['Detractor', 'Weight'].map(h => (
            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.textMuted, fontWeight: 500, borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
          ))}</tr></thead>
          <tbody>{DETRACTOR_WEIGHTS.map(d => (
            <tr key={d.name} style={{ borderBottom: `1px solid ${T.border}` }}>
              <td style={{ padding: '8px 12px', color: T.textPrimary }}>{d.name}</td>
              <td style={{ padding: '8px 12px', color: T.error, fontFamily: 'monospace' }}>{d.weight.toFixed(2)}</td>
            </tr>
          ))}</tbody>
        </table>
      ))}

      {card('Mode Weight Profiles', (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr>{['Mode', 'Portrait', 'Lighting', 'Composition', 'Scene'].map(h => (
            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.textMuted, fontWeight: 500, borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
          ))}</tr></thead>
          <tbody>{Object.entries(MODE_WEIGHTS).map(([mode, w]) => (
            <tr key={mode} style={{ borderBottom: `1px solid ${T.border}` }}>
              <td style={{ padding: '8px 12px', color: T.textPrimary }}>{mode}</td>
              <td style={{ padding: '8px 12px', color: DIM_COLORS.portrait, fontFamily: 'monospace' }}>{w.portrait.toFixed(2)}</td>
              <td style={{ padding: '8px 12px', color: DIM_COLORS.lighting, fontFamily: 'monospace' }}>{w.lighting.toFixed(2)}</td>
              <td style={{ padding: '8px 12px', color: DIM_COLORS.composition, fontFamily: 'monospace' }}>{w.composition.toFixed(2)}</td>
              <td style={{ padding: '8px 12px', color: DIM_COLORS.scene, fontFamily: 'monospace' }}>{w.scene.toFixed(2)}</td>
            </tr>
          ))}</tbody>
        </table>
      ))}

      {card('Patent Claims Summary', (
        <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.8 }}>
          <div style={{ marginBottom: 8 }}><span style={{ color: T.teal, fontWeight: 600 }}>Claim 1:</span> Transparent composite scoring with mode-specific weight profiles</div>
          <div style={{ marginBottom: 8 }}><span style={{ color: T.teal, fontWeight: 600 }}>Claim 3:</span> Landmark spike coefficient system (tier-based multiplicative boost)</div>
          <div style={{ marginBottom: 8 }}><span style={{ color: T.teal, fontWeight: 600 }}>Claim 4:</span> Multiplicative detractor penalty system (not additive)</div>
          <div><span style={{ color: T.teal, fontWeight: 600 }}>Claim 5:</span> Score-card-driven editing pipeline (Score {'\u2192'} Diagnose {'\u2192'} Suggest {'\u2192'} Apply)</div>
        </div>
      ))}
    </div>
  );

  const renderHealth = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>System Health</h2>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
        {['API Gateway', 'Scoring Engine', 'CDN (CloudFront)', 'Database (DynamoDB)'].map(svc => (
          <div key={svc} style={{ background: T.surface, borderRadius: 12, padding: 20, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: T.textPrimary }}>{svc}</div>
              <div style={{ fontSize: 12, color: T.success, marginTop: 4 }}>Operational</div>
            </div>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: T.success, boxShadow: `0 0 8px ${T.success}40` }} />
          </div>
        ))}
      </div>
      {card('Platform Info', (
        <div>
          {[['Environment', 'Production'], ['Region', 'us-east-1'], ['Bundle', 'cloud.sandstone.frame'], ['Scoring Engine', 'v2 (on-device)']].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
              <span style={{ color: T.textMuted }}>{k}</span>
              <span style={{ color: T.textSecondary }}>{v}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderFeedback = () => {
    if (sectionLoading || !feedbackData) return loadingView();
    const items = feedbackData.items || [];
    const newCount = items.filter((f: any) => f.status === 'new').length;
    const thisWeek = items.filter((f: any) => {
      const d = new Date(f.createdAt);
      return Date.now() - d.getTime() < 7 * 24 * 60 * 60 * 1000;
    }).length;
    const statusColor: Record<string, string> = { new: T.teal, reviewed: T.blueLight, resolved: T.success };
    const statusBg: Record<string, string> = { new: 'rgba(80,184,168,0.15)', reviewed: 'rgba(59,130,246,0.15)', resolved: 'rgba(34,197,94,0.15)' };

    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>User Feedback</h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Feedback', value: items.length },
            { label: 'New (Unreviewed)', value: newCount },
            { label: 'This Week', value: thisWeek },
          ].map(s => (
            <div key={s.label} style={{ background: T.surface, borderRadius: 12, padding: 20, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
            </div>
          ))}
        </div>
        {card('All Feedback', (
          <div>
            {items.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No feedback yet.</div>}
            {items.map((f: any) => (
              <div key={f.id} style={{ borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}
                onClick={() => setExpandedFeedback(expandedFeedback === f.id ? null : f.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: statusColor[f.status] || T.textMuted, background: statusBg[f.status] || T.navy }}>
                    {f.status}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: T.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.message}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{f.email}</span>
                  <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0, width: 50, textAlign: 'center', textTransform: 'capitalize' }}>{f.tier}</span>
                  <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0, width: 70 }}>{f.createdAt?.slice(0, 10)}</span>
                </div>
                {expandedFeedback === f.id && (
                  <div style={{ padding: '0 0 12px 0', fontSize: 12, color: T.textSecondary, lineHeight: 1.6 }}>
                    <div style={{ marginBottom: 8 }}>{f.message}</div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: T.textMuted }}>
                      <span>Mode: <strong style={{ color: T.textSecondary }}>{f.mode}</strong></span>
                      {f.context?.userAgent && <span>UA: {f.context.userAgent}</span>}
                      {f.context?.screen && <span>Screen: {f.context.screen}</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderActivity = () => {
    if (sectionLoading || !activityData) return loadingView();
    const a = activityData;

    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>Activity & Engagement</h2>

        {/* Active users */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Daily Active Users', value: a.dau?.toLocaleString() ?? '—' },
            { label: 'Weekly Active Users', value: a.wau?.toLocaleString() ?? '—' },
            { label: 'Monthly Active Users', value: a.mau?.toLocaleString() ?? '—' },
          ].map(s => (
            <div key={s.label} style={{ background: T.surface, borderRadius: 12, padding: 20, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Photos scored */}
        {card('Photos Scored', (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
            {[
              { label: 'Today', value: a.photosToday?.toLocaleString() ?? '—', color: T.teal },
              { label: 'This Week', value: a.photosWeek?.toLocaleString() ?? '—', color: T.blueLight },
              { label: 'All Time', value: a.photosAllTime?.toLocaleString() ?? '—', color: T.textPrimary },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: 16, background: T.navy, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        ))}

        {/* User split + engagement */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {card('User Split', (
            <div>
              <div style={{ display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{ width: `${a.anonymousPercent}%`, background: T.textMuted, transition: 'width 0.4s' }} />
                <div style={{ width: `${a.authenticatedPercent}%`, background: T.teal, transition: 'width 0.4s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: T.textMuted }}>Anonymous: {a.anonymousPercent}%</span>
                <span style={{ color: T.teal }}>Authenticated: {a.authenticatedPercent}%</span>
              </div>
            </div>
          ))}
          {card('Engagement', (
            <div>
              {[
                { label: 'Avg Session Duration', value: `${a.avgSessionDuration?.toFixed(1) ?? '—'} min` },
                { label: 'Free Trial Conversions', value: `${a.freeTrialConversions?.toLocaleString() ?? '—'} (${a.conversionRate ?? 0}%)` },
                { label: 'PRISM Adoption', value: `${a.prismAdoption ?? 0}%` },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
                  <span style={{ color: T.textMuted }}>{s.label}</span>
                  <span style={{ color: T.textPrimary, fontWeight: 500 }}>{s.value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Top modes */}
        {card('Mode Popularity', (
          <div>
            {(a.topModes || []).map((m: any) => (
              <div key={m.mode} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: T.textSecondary }}>{m.mode}</span>
                  <span style={{ color: T.textPrimary, fontWeight: 500 }}>{m.pct}%</span>
                </div>
                <div style={{ background: T.navy, borderRadius: 3, height: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${m.pct}%`, height: '100%', background: T.teal, borderRadius: 3, transition: 'width 0.4s' }} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const sectionMap: Record<NavSection, () => React.ReactNode> = {
    dashboard: renderDashboard, scoring: renderScoring, modes: renderModes,
    users: renderUsers, feedback: renderFeedback, activity: renderActivity,
    calibration: renderCalibration, health: renderHealth,
  };

  // ─── Sidebar ──────────────────────────────────────────────
  const sidebar = (
    <div style={{
      width: 240, background: T.surface, height: '100vh', borderRight: `1px solid ${T.border}`,
      display: 'flex', flexDirection: 'column',
      position: isMobile ? 'fixed' : 'relative', top: 0, left: 0, zIndex: 100,
      transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
      transition: 'transform 0.25s ease',
    }}>
      <a href="/" style={{ textDecoration: 'none', display: 'block', padding: '24px 20px 20px', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.textPrimary, letterSpacing: 2 }}>FRAME</div>
        <div style={{ fontSize: 11, color: T.teal, fontWeight: 500, letterSpacing: 1.2, marginTop: 2 }}>ADMIN CONSOLE</div>
      </a>
      <nav style={{ flex: 1, padding: '12px 8px' }}>
        {navItems.map(item => {
          const active = activeSection === item.key;
          return (
            <button key={item.key} onClick={() => { setActiveSection(item.key); if (isMobile) setSidebarOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 14, fontWeight: active ? 600 : 400,
                background: active ? 'rgba(37,99,235,0.12)' : 'transparent',
                color: active ? T.blueLight : T.textSecondary, textAlign: 'left', transition: 'all 0.15s', marginBottom: 2,
              }}>
              <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
              {item.label}
            </button>
          );
        })}
      </nav>
      <div style={{ padding: '16px 20px', borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.textMuted }}>
        <div style={{ marginBottom: 4 }}>{email || 'Admin'}</div>
        <button onClick={handleSignOut} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: FONT, textDecoration: 'underline' }}>
          Sign out
        </button>
      </div>
    </div>
  );

  const overlay = isMobile && sidebarOpen ? (
    <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
  ) : null;

  // ─── Main layout ──────────────────────────────────────────
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.navy, fontFamily: FONT, color: T.textPrimary }}>
      {overlay}
      {sidebar}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: `1px solid ${T.border}`, background: T.surface, gap: 10 }}>
          {isMobile && (
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textPrimary, fontSize: 20, padding: 4 }} aria-label="Toggle menu">
              {'\u2630'}
            </button>
          )}
          <a href="/" style={{
            background: 'none', border: `1px solid ${T.border}`, borderRadius: 8,
            padding: '5px 12px', color: T.teal, fontSize: 13, fontWeight: 600,
            textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4,
            whiteSpace: 'nowrap',
          }}>
            &larr; Home
          </a>
          <div style={{ fontSize: 15, fontWeight: 500, color: T.textSecondary }}>
            {navItems.find(n => n.key === activeSection)?.label}
          </div>
          <div style={{ flex: 1 }} />
          {usingFallback && (
            <div style={{ fontSize: 11, color: T.teal, background: 'rgba(80,184,168,0.1)', padding: '4px 10px', borderRadius: 6, fontWeight: 500 }}>
              Using demo data — API unavailable
            </div>
          )}
          <div style={{ fontSize: 12, color: T.textMuted, display: isMobile ? 'none' : 'block' }}>Sandstone Cloud</div>
        </header>
        <main style={{ flex: 1, padding: isMobile ? 16 : 32, overflowY: 'auto' }}>
          {sectionMap[activeSection]()}
        </main>
      </div>
    </div>
  );
}
